import type { TCollabThread } from '@tiptap-pro/provider'
import { useMemo } from 'react'
import type { ThreadCommentData, ThreadData } from '../../hooks/useThreads'

type UseAnchorParams = {
  thread: TCollabThread<ThreadData, ThreadCommentData>
  /**
   * The DOM element representing the editor view.
   * Typically accessed via `editor.view.dom`.
   */
  viewElement: HTMLElement
}

export type ThreadAnchor = {
  /**
   * An array of the DOM elements representing the thread decoration.
   */
  elements: HTMLElement[]
  /**
   * The text content of the thread highlight area.
   */
  textContent: string
  /**
   * A specific DOM element that can be used to track the position of
   * the thread decoration. Used by Floating UI.
   *
   * @see https://floating-ui.com/docs/virtual-elements#contextelement
   */
  contextElement: Element | undefined
  /**
   * Returns a bounding box that encompasses all elements in
   * the `elements` array.
   */
  getBoundingClientRect: () => DOMRect
  /**
   * Returns an array of all client rects among all elements.
   * A single inline element may have multiple client rects if it's
   * split across multiple lines.
   */
  getClientRects: () => DOMRect[]
}

/**
 * Fits a DOMRect bounding box around all of the given client rects.
 */
const getBoundingClientRect = (clientRects: DOMRect[]): DOMRect => {
  const minX = Math.min(...clientRects.map((rect) => rect.left))
  const minY = Math.min(...clientRects.map((rect) => rect.top))
  const maxX = Math.max(...clientRects.map((rect) => rect.right))
  const maxY = Math.max(...clientRects.map((rect) => rect.bottom))

  return new DOMRect(minX, minY, maxX - minX, maxY - minY)
}

export const useThreadAnchor = ({
  thread,
  viewElement,
}: UseAnchorParams): ThreadAnchor => {
  const elements = Array.from(
    viewElement.querySelectorAll<HTMLElement>(`[data-thread-id="${thread.id}"]`)
  )

  const anchor = useMemo<ThreadAnchor>(() => {
    const textContent = elements
      .reduce((str, element) => str + element.textContent + ' ', '')
      .trim()

    const getClientRects = () =>
      elements.flatMap((element) => Array.from(element.getClientRects()))

    return {
      elements,
      textContent,
      contextElement: elements?.[0],
      getClientRects,
      getBoundingClientRect: () => getBoundingClientRect(getClientRects()),
    }
  }, [elements])

  return anchor
}
